import type { DashboardParam } from '../types';

/**
 * Known-bad stringification artifacts from JS → URL coercion.
 * URLSearchParams.set(key, undefined) produces "undefined" as a string.
 */
export const INVALID_PARAM_VALUES = new Set(['undefined', 'null', 'NaN']);

/**
 * Validate that a string is a real calendar date in strict YYYY-MM-DD shape.
 *
 * Why strict shape: `displayDate` in DashboardParamBar assumes YYYY-MM-DD and
 * will render nonsense like "undefined NaN, bogus" for any other format. We
 * also reject values like "2026-13-45" that pass the regex but aren't real
 * dates (month > 12, day > 31, Feb 30, …) by round-tripping through Date().
 */
export function isValidDateString(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** Format a Date as YYYY-MM-DD. */
export function formatDateISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Compute default param values from a dashboard spec's parameters. */
export function computeDefaults(parameters: DashboardParam[]): Record<string, string | string[]> {
  const defaults: Record<string, string | string[]> = {};
  for (const param of parameters) {
    if (param.type === 'time_range') {
      const now = new Date();
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      defaults[`${param.name}_start`] = formatDateISO(thirtyDaysAgo);
      defaults[`${param.name}_end`] = formatDateISO(now);
    } else if (param.default !== undefined) {
      defaults[param.name] = param.default;
    } else if (param.type === 'select' && param.options?.[0]) {
      defaults[param.name] = param.options[0].value;
    } else if (param.type === 'multi_select') {
      defaults[param.name] = [];
    }
  }
  return defaults;
}

/** Parse search params into a param values record, rejecting known-bad values. */
export function searchParamsToValues(
  searchParams: URLSearchParams,
  parameters: DashboardParam[],
): Record<string, string | string[]> {
  const values: Record<string, string | string[]> = {};
  for (const param of parameters) {
    if (param.type === 'time_range') {
      // Reject anything that isn't a strict YYYY-MM-DD real date — the
      // downstream display and SQL-substitution layers both assume that
      // shape. Falling through to `computeDefaults` is the safe default.
      const startKey = `${param.name}_start`;
      const endKey = `${param.name}_end`;
      const s = searchParams.get(startKey);
      const e = searchParams.get(endKey);
      if (s && isValidDateString(s)) values[startKey] = s;
      if (e && isValidDateString(e)) values[endKey] = e;
    } else if (param.type === 'multi_select') {
      const raw = searchParams.getAll(param.name);
      const filtered = raw.filter((v) => !INVALID_PARAM_VALUES.has(v));
      if (filtered.length > 0) {
        values[param.name] = filtered;
      }
    } else {
      const raw = searchParams.get(param.name);
      if (raw !== null && !INVALID_PARAM_VALUES.has(raw)) {
        values[param.name] = raw;
      }
    }
  }
  return values;
}

/** Serialize param values to URLSearchParams, only writing actual strings. */
export function valuesToSearchParams(values: Record<string, string | string[]>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, val] of Object.entries(values)) {
    if (Array.isArray(val)) {
      for (const v of val) {
        if (typeof v === 'string' && v !== '') params.append(key, v);
      }
    } else if (typeof val === 'string' && val !== '') {
      params.set(key, val);
    }
  }
  return params;
}
